<template>
    <div class="accounts-container container">
      <el-card shadow="hover">
        <template #header>
          <div class="card-header">
            <h2>إدارة شجرة الحسابات</h2>
            <el-button type="primary" @click="handleCreate(null)">
              <el-icon><Plus /></el-icon> إضافة حساب رئيسي جديد
            </el-button>
          </div>
        </template>
  
        <el-alert
          title="ما هي شجرة الحسابات؟"
          type="success"
          description="شجرة الحسابات هي الخريطة المالية الكاملة للمدرسة. هنا، تقوم بتعريف جميع الحسابات التي ستستخدمها لتتبع الأموال، مثل 'الأصول' (النقدية والبنوك)، 'الإيرادات' (رسوم الطلاب)، و'المصروفات' (الرواتب والمشتريات). بناء هذه الشجرة بشكل صحيح هو أساس التقارير المالية الدقيقة."
          :closable="false"
          show-icon
          style="margin-bottom: 20px;"
        />
  
        <el-tree
          :data="accountsTree"
          :props="treeProps"
          node-key="id"
          default-expand-all
          :expand-on-click-node="false"
          v-loading="loading"
        >
          <template #default="{ node, data }">
            <span class="custom-tree-node">
              <span>{{ node.label }} ({{ data.type }})</span>
              <span>
                <el-button size="small" type="text" @click="handleCreate(data)">إضافة حساب فرعي</el-button>
                <el-button size="small" type="text" @click="handleEdit(data)">تعديل</el-button>
                <el-button size="small" type="text" style="color: #F56C6C;" @click="handleDelete(data)">حذف</el-button>
              </span>
            </span>
          </template>
        </el-tree>
      </el-card>
  
      <!-- Dialog لإنشاء/تعديل الحساب -->
      <el-dialog v-model="dialogVisible" :title="dialogTitle" width="40%" :close-on-click-modal="false">
        <el-form :model="form" ref="formRef" label-position="top" :rules="rules">
          <el-form-item label="اسم الحساب" prop="name">
            <el-input v-model="form.name" placeholder="مثال: صندوق النقدية الرئيسي" />
          </el-form-item>
          <el-form-item label="نوع الحساب" prop="type">
            <el-select v-model="form.type" placeholder="اختر النوع" style="width:100%">
              <el-option label="أصل (Asset)" value="asset"></el-option>
              <el-option label="خصم (Liability)" value="liability"></el-option>
              <el-option label="حقوق ملكية (Equity)" value="equity"></el-option>
              <el-option label="إيراد (Income)" value="income"></el-option>
              <el-option label="مصروف (Expense)" value="expense"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="الحساب الرئيسي (إن وجد)" prop="parent_id">
              <el-select v-model="form.parent_id" placeholder="اتركه فارغًا لجعله حسابًا رئيسيًا" clearable style="width:100%">
                  <el-option v-for="account in flatAccounts" :key="account.id" :label="account.name" :value="account.id" />
              </el-select>
          </el-form-item>
        </el-form>
        <template #footer>
          <span class="dialog-footer">
            <el-button @click="dialogVisible = false">إلغاء</el-button>
            <el-button type="primary" @click="submitForm" :loading="formLoading">
              {{ form.id ? 'تحديث' : 'إنشاء' }}
            </el-button>
          </span>
        </template>
      </el-dialog>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { ElMessage } from 'element-plus';
  import { Plus } from '@element-plus/icons-vue';
  
  export default {
    name: 'ChartOfAccounts',
    components: { Plus },
    data() {
      return {
        api: null,
        loading: true,
        accountsTree: [],
        flatAccounts: [],
        dialogVisible: false,
        formLoading: false,
        form: this.getInitialForm(),
        rules: {
          name: [{ required: true, message: 'اسم الحساب مطلوب', trigger: 'blur' }],
          type: [{ required: true, message: 'نوع الحساب مطلوب', trigger: 'change' }],
        },
        treeProps: {
          children: 'children',
          label: 'name',
        },
      };
    },
    computed: {
      dialogTitle() {
        return this.form.id ? 'تعديل الحساب' : 'إنشاء حساب جديد';
      },
    },
    created() {
      this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
      this.api.interceptors.request.use((config) => {
        const token = localStorage.getItem('access_token');
        if (token) config.headers['Authorization'] = `Bearer ${token}`;
        return config;
      });
      this.fetchAccounts();
    },
    methods: {
      getInitialForm() {
        return { id: null, name: '', type: null, parent_id: null };
      },
      flattenAccounts(accounts, flatList = []) {
          accounts.forEach(account => {
              flatList.push(account);
              if (account.children) {
                  this.flattenAccounts(account.children, flatList);
              }
          });
          return flatList;
      },
      async fetchAccounts() {
        this.loading = true;
        try {
          const response = await this.api.get('/accounts');
          this.accountsTree = response.data;
          this.flatAccounts = this.flattenAccounts(response.data);
        } catch (error) {
          ElMessage.error('فشل في جلب شجرة الحسابات.');
        } finally {
          this.loading = false;
        }
      },
      handleCreate(parentAccount) {
        this.form = this.getInitialForm();
        if (parentAccount) {
            this.form.parent_id = parentAccount.id;
            this.form.type = parentAccount.type; // Inherit type from parent
        }
        this.dialogVisible = true;
      },
      handleEdit(account) {
        this.form = { ...account };
        this.dialogVisible = true;
      },
      async submitForm() {
        if (!this.$refs.formRef) return;
        await this.$refs.formRef.validate(async (valid) => {
          if (!valid) return;
          this.formLoading = true;
          try {
            if (this.form.id) {
              await this.api.put(`/accounts/${this.form.id}`, this.form);
              ElMessage.success('تم تحديث الحساب بنجاح!');
            } else {
              await this.api.post('/accounts', this.form);
              ElMessage.success('تم إنشاء الحساب بنجاح!');
            }
            this.dialogVisible = false;
            this.fetchAccounts();
          } catch (error) {
            const errorMsg = error.response?.data?.errors ? Object.values(error.response.data.errors)[0][0] : (error.response?.data?.message || 'فشلت العملية.');
            ElMessage.error(errorMsg);
          } finally {
            this.formLoading = false;
          }
        });
      },
      async handleDelete(account) {
          this.$confirm(`هل أنت متأكد من حذف الحساب "${account.name}"؟`, 'تأكيد الحذف', {
              confirmButtonText: 'نعم',
              cancelButtonText: 'إلغاء',
              type: 'warning',
          }).then(async () => {
              try {
                  await this.api.delete(`/accounts/${account.id}`);
                  ElMessage.success('تم حذف الحساب بنجاح!');
                  this.fetchAccounts();
              } catch (error) {
                  ElMessage.error(error.response?.data?.message || 'فشل في حذف الحساب.');
              }
          }).catch(() => {
              // User cancelled
          });
      },
    },
  };
  </script>
  
  <style scoped>
  .accounts-container { padding: 20px; }
  .card-header { display: flex; justify-content: space-between; align-items: center; }
  .custom-tree-node {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 14px;
    padding-right: 8px;
  }
  </style>
  